import _ from "lodash";
import { SaleBidder, SaleContactBidder } from "../bidders/BidderData";
import { SaleLot } from "../lots/LotData";
import { ContactCredit } from "../operation/contacts/credits/ContactCreditData";
import { SaleDiscount } from "../sales/discounts/SaleDiscountData";
import { Payment } from "./payments/InvoicePaymentData";
import dayjs from "dayjs";
import { AgTechContact } from "@agtech/data/contacts/ContactData";
import { ContactNote } from "@agtech/data/contacts/notes/ContactNoteData";
import { AgTechIndex } from "@agtech/core/utilities/AgTechUtilities";

export type BidderLotSaleReadyForInvoicing = {
    id: number,
    bidder: SaleBidder,
    numberOfLots: number,
    totalPurchasePrice: number
}

export type InvoiceInfo = {
    id: number,
    invoiceNumber: string,
    invoiceTotal: number,
    numberOfLots: number,
    numberOfDiscountsApplied?: number,
    numberOfCreditsApplied?: number,
    purchaserName: string,
    invoiceDate: Date,
    contactId: number,
    contactBidder: SaleContactBidder
}

export enum InvoiceStatus {
    'Paid',
    'Unpaid',
    'Partially Paid'
}

export type InvoiceDetails = {
    invoice: {
        id: number,
        status: string,
        info: {
            date: string,
            number: string,
            total: number,
            status: InvoiceStatus
        },
        details: {
            creditsApplied: ContactCredit[],
            discountsGiven: SaleDiscount[],
            lotsPurchased: SaleLot[],
            paymentsMade: Payment[]
        }
    },
    contact: {
        id: number,
        info: AgTechContact,
        details: {
            notes: ContactNote[],
            credits: ContactCredit[]
        }
    }
}

export type InvoiceTotal = {
    lotPurchaseTotal: number,
    appliedCreditsTotal: number,
    appliedDiscountsTotal: number,
    invoiceTotal: number
}

const calculateLotPurchaseTotal = (selectedLots: SaleLot[]): number => {
    return _.sum(selectedLots.map(l => (l.purchasePrice ?? 0) * (l.groupSize ?? 1)));
}

const calculateCreditTotal = (selectedCredits: ContactCredit[]): number => {
    return _.sum(selectedCredits.map(c => c.creditAmount ?? 0));
}

export const calculateDiscountTotal = (sumOfLotsSold: number, appliedDiscounts: SaleDiscount[]): number => {
    let invoiceDiscountTotal = 0;

    appliedDiscounts.forEach(d => {
        let discountAmount = d.isPercentage
            ? ((d.amount / 100.0) * sumOfLotsSold)
            : d.amount;

        invoiceDiscountTotal += discountAmount;
    });

    return invoiceDiscountTotal;
}

export const calculateInvoiceTotal = (props: {
    selectedLots: SaleLot[],
    appliedDiscounts: SaleDiscount[],
    appliedCredits: ContactCredit[]
}): InvoiceTotal => {
    const lotPurchaseTotal = calculateLotPurchaseTotal(props.selectedLots);
    const creditTotal = calculateCreditTotal(props.appliedCredits);
    const discountTotal = calculateDiscountTotal(lotPurchaseTotal, props.appliedDiscounts);

    return {
        lotPurchaseTotal,
        appliedCreditsTotal: creditTotal,
        appliedDiscountsTotal: discountTotal,
        invoiceTotal: lotPurchaseTotal - discountTotal - creditTotal
    }
}

export const getInvoiceFileName = (props: {
    invoiceContact: AgTechContact,
    invoiceNumber: string,
    invoiceDate: string
}) => {
    return `Invoice_${props.invoiceNumber}_${props.invoiceContact.firstName}_${props.invoiceContact.lastName}_${dayjs(props.invoiceDate).format('MM_DD_YYYY')}.pdf`
}